import Portal from "elements/Portal";
import { WEBSITE_BUILDER_Z_LAYERS } from "pages/secure/facility/websiteBuilder/editor/utility/websiteBuilderZLayers";
import { WEBSITE_BUILDER_DRAG_LAYER } from "pages/secure/facility/websiteBuilder/editor/WebsiteBuilderEditor";
import React, { useEffect, useMemo, useState } from "react";
import { TWebsiteBuilderTableCell, TWebsiteBuilderTableRow, WEBSITE_BUILDER_TABLE_HEAD_ROW_ID, websiteBuilderGenerateTableCellDesignStyles, websiteBuilderGenerateTableDesignStyles, websiteBuilderGenerateTableRowDesignStyles } from "pages/secure/facility/websiteBuilder/editor/nodes/container/websiteBuilderTable";
import { clamp } from "lodash";
import { useAppSelector } from "hooks/redux";
import { useTableBuilderContext } from "../TableBuilderContext";
import "./tableBuilderOverlay.scss";
import { websiteBuilderFindIconAsset, WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG } from "pages/secure/facility/websiteBuilder/editor/nodes/element/websiteBuilderIcon";

interface ITableBuilderOverlayRowDragLayer {
  topBoundary: number;
  bottomBoundary: number;
  left: number;
  width: number;
  height: number;
  tableInnerHTML: string;
}

interface ITableBuilderOverlayColumnDragLayer {
  leftBoundary: number;
  rightBoundary: number;
  top: number;
  width: number;
  height: number;
  tableInnerHTML: string;
}

interface ITableBuilderOverlayDragColumnCell {
  parentRow: TWebsiteBuilderTableRow;
  parentRowHeight: number;
  cell: TWebsiteBuilderTableCell;
  cellWidth: number;
}

type TTableBuilderOverlayDragColumn = ITableBuilderOverlayDragColumnCell[];

export function TableBuilderOverlay() {
  const { dragLayerContext, containerBoundingBox, tableNode, tableRef, domNodeRefMap } = useTableBuilderContext();

  const rootCssVariables = useAppSelector(store => store.websiteBuilderStore.editor.rootCssVariables);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const [rowDragLayer, setRowDragLayer] = useState<ITableBuilderOverlayRowDragLayer>(null);
  const [columnDragLayer, setColumnDragLayer] = useState<ITableBuilderOverlayColumnDragLayer>(null);

  const rowOverlayActive = dragLayerContext.type === "row-active" && rowDragLayer != null;
  const columnOverlayActive = dragLayerContext.type === "column-active" && columnDragLayer != null;
  
  const draggingBodyRow = dragLayerContext.type === "row-active" ? dragLayerContext.bodyRow : null;
  const draggingColumnIndex = dragLayerContext.type === "column-active" ? dragLayerContext.columnIndex : null;

  const tableDesignStyles = websiteBuilderGenerateTableDesignStyles();
  
  const tableNodeSettings = tableNode.container.settings;

  const tableNodeRows: TWebsiteBuilderTableRow[] = useMemo(() => {
    return [tableNodeSettings.head.row, ...tableNodeSettings.body.rows];
  }, [
    tableNodeSettings.head.row,
    tableNodeSettings.body.rows,
  ]);

  useEffect(() => {
    if (dragLayerContext.type === "row-active") {
      const domNode = domNodeRefMap.current.get(draggingBodyRow.id);
      const headRowDomNode = domNodeRefMap.current.get(WEBSITE_BUILDER_TABLE_HEAD_ROW_ID);
      const tableDomNode = tableRef.current;
      
      if (domNode != null && headRowDomNode != null && tableDomNode != null) {
        const table = document.createElement("table");
        const tbody = document.createElement("tbody");
        const tr = document.createElement("tr");

        const rowDesignStyles = websiteBuilderGenerateTableRowDesignStyles(draggingBodyRow);

        tr.style.cssText = rowDesignStyles.styleDeclaration;

        draggingBodyRow.cells.forEach(cell => {
          const td = document.createElement("td");
          const content = document.createElement("div");

          const cellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(cell);

          if (cell.value.type === "icon") {
            content.className = `${cellDesignStyles.contentClassName} ${cellDesignStyles.iconClassName}`;
            content.style.cssText = cellDesignStyles.iconStyleDeclaration;

            const iconAsset = websiteBuilderFindIconAsset(iconAssets, cell.value.assetIdentity);

            content.innerHTML = iconAsset?.svg ?? (cell.value.assetIdentity != null ? WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG : "");
          } else {
            content.className = cellDesignStyles.contentClassName;
            content.textContent = cell.value.content;
          }

          td.appendChild(content);
          tr.appendChild(td);
        });

        tbody.appendChild(tr);
        table.appendChild(tbody);

        const boundingRect = domNode.getBoundingClientRect();
        const headRowBoundingRect = headRowDomNode.getBoundingClientRect();
        const tableBoundingRect = tableDomNode.getBoundingClientRect();

        const topBoundary = headRowBoundingRect.bottom + (boundingRect.height / 2);
        const bottomBoundary = tableBoundingRect.top + containerBoundingBox.height - (boundingRect.height / 2);

        setRowDragLayer({
          topBoundary,
          bottomBoundary,
          left: boundingRect.left,
          width: boundingRect.width,
          height: boundingRect.height,
          tableInnerHTML: table.innerHTML,
        });
      }
    }

    return () => {
      if (dragLayerContext.type === "row-active") {
        setRowDragLayer(null);
      }
    };
  }, [dragLayerContext.type, draggingBodyRow, containerBoundingBox.height, iconAssets]);

  useEffect(() => {
    if (dragLayerContext.type === "column-active") {
      const dragColumn: TTableBuilderOverlayDragColumn = tableNodeRows.map(row => {
        return (
          {
            parentRow: row,
            parentRowHeight: domNodeRefMap.current.get(row.id)?.getBoundingClientRect()?.height,
            cell: row.cells[draggingColumnIndex],
            cellWidth: domNodeRefMap.current.get(row.cells[draggingColumnIndex]?.id)?.getBoundingClientRect()?.width,
          }
        );
      });

      const tableDomNode = tableRef.current;

      if (
        dragColumn.every(dragCell => (
          dragCell.parentRow != null &&
          dragCell.parentRowHeight != null &&
          dragCell.cell != null &&
          dragCell.cellWidth != null
        )) &&
        tableDomNode != null
      ) {
        const table = document.createElement("table");
        const thead = document.createElement("thead");
        const tbody = document.createElement("tbody");

        dragColumn.forEach((dragCell, index) => {
          const rowDesignStyles = websiteBuilderGenerateTableRowDesignStyles(dragCell.parentRow);
          const cellDesignStyles = websiteBuilderGenerateTableCellDesignStyles(dragCell.cell);

          const tr = document.createElement("tr");
          tr.style.cssText = rowDesignStyles.styleDeclaration;
          tr.style.height = `${dragCell.parentRowHeight}px`;

          const content = document.createElement("div");

          if (dragCell.cell.value.type === "icon") {
            content.className = `${cellDesignStyles.contentClassName} ${cellDesignStyles.iconClassName}`;
            content.style.cssText = cellDesignStyles.iconStyleDeclaration;

            const iconAsset = websiteBuilderFindIconAsset(iconAssets, dragCell.cell.value.assetIdentity);

            content.innerHTML = iconAsset?.svg ?? (dragCell.cell.value.assetIdentity != null ? WEBSITE_BUILDER_MISSING_ICON_ASSET_SVG : "");
          } else {
            content.className = cellDesignStyles.contentClassName;
            content.textContent = dragCell.cell.value.content;
          }

          if (index === 0) {
            const th = document.createElement("th");

            th.appendChild(content);
            tr.appendChild(th);
            thead.appendChild(tr);
          } else {
            const td = document.createElement("td");
            
            td.appendChild(content);
            tr.appendChild(td);
            tbody.appendChild(tr);
          }
        });

        table.appendChild(thead);
        table.appendChild(tbody);

        const tableBoundingRect = tableDomNode.getBoundingClientRect();
        
        const cellWidth = dragColumn[0].cellWidth;
        const halfCellWidth = cellWidth / 2;

        setColumnDragLayer({
          leftBoundary: tableBoundingRect.left + halfCellWidth,
          rightBoundary: tableBoundingRect.right - halfCellWidth,
          top: tableBoundingRect.top,
          width: cellWidth,
          height: containerBoundingBox.height,
          tableInnerHTML: table.innerHTML,
        });
      }
    }

    return () => {
      if (dragLayerContext.type === "column-active") {
        setColumnDragLayer(null);
      }
    };
  }, [dragLayerContext.type, tableNodeRows, draggingColumnIndex, containerBoundingBox.height, iconAssets]);

  return (
    <>
      {rowOverlayActive && (
        <Portal isMounted={true} parentId={WEBSITE_BUILDER_DRAG_LAYER}>
          <div
            className="table-builder-overlay-drag-layer row-drag-layer"
            style={{
              left: rowDragLayer.left,
              top: clamp(dragLayerContext.clientOffset.y, rowDragLayer.topBoundary, rowDragLayer.bottomBoundary),
              width: rowDragLayer.width,
              height: rowDragLayer.height,
              zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
              ...rootCssVariables,
            }}
          >
            <div className={tableDesignStyles.containerClassName}>
              <table
                className={tableDesignStyles.className}
                dangerouslySetInnerHTML={{ __html: rowDragLayer.tableInnerHTML }}
              />
            </div>
          </div>
          <div
            className="table-builder-overlay-target row-target"
            style={{
              left: rowDragLayer.left,
              top: dragLayerContext.target?.y,
              width: rowDragLayer.width,
              zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
            }}
          />
        </Portal>
      )}
      {columnOverlayActive && (
        <Portal isMounted={true} parentId={WEBSITE_BUILDER_DRAG_LAYER}>
          <div
            className="table-builder-overlay-drag-layer column-drag-layer"
            style={{
              left: clamp(dragLayerContext.clientOffset.x, columnDragLayer.leftBoundary, columnDragLayer.rightBoundary),
              top: columnDragLayer.top,
              width: columnDragLayer.width,
              height: columnDragLayer.height,
              zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
              ...rootCssVariables,
            }}
          >
            <div className={tableDesignStyles.containerClassName}>
              <table
                className={tableDesignStyles.className}
                dangerouslySetInnerHTML={{ __html: columnDragLayer.tableInnerHTML }}
              />
            </div>
          </div>
          <div
            className="table-builder-overlay-target column-target"
            style={{
              left: dragLayerContext.target?.x,
              top: columnDragLayer.top,
              height: columnDragLayer.height,
              zIndex: WEBSITE_BUILDER_Z_LAYERS.dragLayer,
            }}
          />
        </Portal>
      )}
    </>
  );
}